import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Modal } from '../../../components/Modal/Modal';
import { Button } from '../../../components/Button/Button';
import Input from '../../../components/Input/Input';
import { StepWrapper } from '../../../components/StepWrapper/StepWrapper';
import { AnalysisTreePanel } from '../../../components/AnalysisTreePanel/AnalysisTreePanel';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../../context/ToastContext';
import { useAuth } from '../../../context/AuthContext';
import FmeaAiagVdaApi from '../../../services/FmeaAiagVdaApi';
import {
  FunctionAnalysisTreeDto,
  FunctionAnalysisItemNode,
  FunctionAnalysisStepNode,
  FunctionAnalysisElementNode,
  FunctionAnalysisFunctionDto,
  FunctionRequirementDto,
} from '../../../interfaces/FmeaStep3';
import { Cause6MEnum } from '../../../interfaces/FmeaStep2';
import styles from './Step3.module.css';

const causeIconMap: Record<Cause6MEnum, string> = {
  [Cause6MEnum.Man]: 'person',
  [Cause6MEnum.Machine]: 'memory',
  [Cause6MEnum.Method]: 'menu_book',
  [Cause6MEnum.Material]: 'inventory_2',
  [Cause6MEnum.Measurement]: 'straighten',
  [Cause6MEnum.Environment]: 'factory',
};

enum FunctionTargetTypeEnum {
  ProcessItem = 1,
  ProcessStep = 2,
  ProcessElement = 3,
}

type NodeSelection =
  | { type: 'item'; itemId: string }
  | { type: 'step'; itemId: string; stepId: string }
  | { type: 'element'; itemId: string; stepId: string; elementId: string };

type SelectionContext =
  | { type: 'item'; item: FunctionAnalysisItemNode }
  | { type: 'step'; item: FunctionAnalysisItemNode; step: FunctionAnalysisStepNode }
  | { type: 'element'; item: FunctionAnalysisItemNode; step: FunctionAnalysisStepNode; element: FunctionAnalysisElementNode };

interface RawFunctionAnalysisRequirement {
  id?: string;
  requirementId?: string;
  description?: string;
  requirementDescription?: string;
  createdAt?: string;
  created_at?: string;
  createdBy?: string;
  created_by?: string;
}

interface RawFunctionAnalysisFunction {
  id?: string;
  functionId?: string;
  description?: string;
  functionDescription?: string;
  functionType?: number | string | null;
  type?: number | string | null;
  requirements?: RawFunctionAnalysisRequirement[];
  associationLevel?: number;
  referenceId?: string;
}

interface RawFunctionAnalysisElement {
  elementId?: string;
  id?: string;
  name?: string;
  description?: string;
  elementDescription?: string;
  cause6m?: number | string | null;
  functions?: RawFunctionAnalysisFunction[];
}

interface RawFunctionAnalysisStep {
  processStepId?: string;
  id?: string;
  processStepName?: string;
  name?: string;
  processStepCode?: string;
  code?: string;
  functions?: RawFunctionAnalysisFunction[];
  elements?: RawFunctionAnalysisElement[];
}

interface RawFunctionAnalysisItem {
  itemId?: string;
  id?: string;
  itemName?: string;
  name?: string;
  itemCode?: string;
  code?: string;
  functions?: RawFunctionAnalysisFunction[];
  processSteps?: RawFunctionAnalysisStep[];
  steps?: RawFunctionAnalysisStep[];
}

interface RawFunctionAnalysisTree {
  stepId?: string;
  analysisId?: string;
  completed?: boolean;
  completedAt?: string;
  completedBy?: string;
  items?: RawFunctionAnalysisItem[];
}

const generateFallbackId = (prefix: string): string => `${prefix}-${Math.random().toString(36).slice(2, 10)}`;

const pickId = (value: unknown, prefix: string): string => {
  if (value !== undefined && value !== null) {
    const strValue = String(value).trim();
    if (strValue) {
      return strValue;
    }
  }
  return generateFallbackId(prefix);
};

const buildDisplayName = (name?: string | null, code?: string | null, fallback: string = ''): string => {
  const label = typeof name === 'string' ? name.trim() : '';
  const codeValue = typeof code === 'string' ? code.trim() : '';
  if (codeValue && label) {
    return `${codeValue} - ${label}`;
  }
  if (label) return label;
  if (codeValue) return codeValue;
  return fallback;
};

const normalizeCauseValue = (value: any): Cause6MEnum | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isNaN(value) ? null : ((Cause6MEnum as any)[value] !== undefined ? (value as Cause6MEnum) : null);
  }
  const numeric = Number(value);
  if (!Number.isNaN(numeric) && (Cause6MEnum as any)[numeric] !== undefined) {
    return numeric as Cause6MEnum;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const lower = trimmed.toLowerCase();
    const matchKey = Object.keys(Cause6MEnum)
      .filter(key => Number.isNaN(Number(key)))
      .find(key => key.toLowerCase() === lower);
    if (matchKey) {
      const mapped = (Cause6MEnum as any)[matchKey];
      if (typeof mapped === 'number') {
        return mapped as Cause6MEnum;
      }
    }
  }
  return null;
};

const mapRequirements = (
  requirements: RawFunctionAnalysisRequirement[] | undefined,
  functionId: string,
): FunctionRequirementDto[] => (
  Array.isArray(requirements)
    ? requirements.map((requirement, index) => {
        const id = pickId(requirement.requirementId ?? requirement.id, `${functionId}-req-${index}`);
        const description = requirement.requirementDescription ?? requirement.description ?? '';
        return {
          id,
          description,
          createdAt: requirement.createdAt ?? requirement.created_at,
          createdBy: requirement.createdBy ?? requirement.created_by,
        };
      })
    : []
);

const mapFunctions = (
  functions: RawFunctionAnalysisFunction[] | undefined,
  parentId: string,
): FunctionAnalysisFunctionDto[] => (
  Array.isArray(functions)
    ? functions.map((fn, index) => {
        const fnId = pickId(fn.functionId ?? fn.id, `${parentId}-fn-${index}`);
        return {
          id: fnId,
          description: fn.functionDescription ?? fn.description ?? '',
          functionType: fn.functionType ?? fn.type ?? null,
          requirements: mapRequirements(fn.requirements, fnId),
          associationLevel: fn.associationLevel != null ? String(fn.associationLevel) : undefined,
          referenceId: fn.referenceId,
        };
      })
    : []
);

const mapElements = (
  elements: RawFunctionAnalysisElement[] | undefined,
  stepId: string,
): FunctionAnalysisElementNode[] => (
  Array.isArray(elements)
    ? elements.map((element, index) => {
        const elementId = pickId(element.elementId ?? element.id, `${stepId}-el-${index}`);
        const description = element.elementDescription ?? element.description ?? element.name ?? '';
        const cause = normalizeCauseValue(
          (element as any).cause6m ??
            (element as any).cause6M ??
            (element as any).cause ??
            (element as any).causeId ??
            (element as any).cause6mId ??
            null,
        );
        return {
          id: elementId,
          name: buildDisplayName(description, undefined, 'Elemento'),
          description: description || undefined,
          cause6m: cause,
          functions: mapFunctions(element.functions, elementId),
        };
      })
    : []
);

const mapSteps = (
  steps: RawFunctionAnalysisStep[] | undefined,
  itemId: string,
): FunctionAnalysisStepNode[] => (
  Array.isArray(steps)
    ? steps.map((step, index) => {
        const stepId = pickId(step.processStepId ?? step.id, `${itemId}-step-${index}`);
        const code = step.processStepCode ?? step.code;
        const name = buildDisplayName(step.processStepName ?? step.name, code, 'Etapa');
        return {
          id: stepId,
          name,
          code: code || undefined,
          functions: mapFunctions(step.functions, stepId),
          elements: mapElements(step.elements, stepId),
        };
      })
    : []
);

const mapItems = (items: RawFunctionAnalysisItem[] | undefined): FunctionAnalysisItemNode[] => (
  Array.isArray(items)
    ? items.map((item, index) => {
        const itemId = pickId(item.itemId ?? item.id, `item-${index}`);
        const code = item.itemCode ?? item.code;
        const name = buildDisplayName(item.itemName ?? item.name, code, 'Item');
        const steps = mapSteps(item.processSteps ?? item.steps, itemId);
        return {
          id: itemId,
          name,
          code: code || undefined,
          functions: mapFunctions(item.functions, itemId),
          steps,
        };
      })
    : []
);

const mapFunctionAnalysisTree = (raw: RawFunctionAnalysisTree | null | undefined): FunctionAnalysisTreeDto => {
  const stepId = raw?.stepId ?? raw?.analysisId ?? undefined;
  return {
    stepId,
    analysisId: raw?.analysisId ?? raw?.stepId ?? undefined,
    items: mapItems(raw?.items),
    completed: raw?.completed ?? undefined,
    completedAt: raw?.completedAt,
    completedBy: raw?.completedBy,
  };
};

const getEntityName = (entity: any, fallback: string): string => {
  if (!entity) return fallback;
  return (
    entity.name ??
    entity.title ??
    entity.description ??
    entity.operationName ??
    entity.elementDescription ??
    fallback
  );
};

const mapToTreeNodes = (
  items: FunctionAnalysisItemNode[]
): any[] => {
  const getCountBadges = (functions?: FunctionAnalysisFunctionDto[]): React.ReactNode => {
    if (!functions) return null;
    const fCount = functions.length;
    const rCount = functions.reduce((acc, fn) => acc + (fn.requirements?.length ?? 0), 0);
    return (
      <div className={styles.badgeGroup}>
        <span className={styles.badge}>{`F: ${fCount}`}</span>
        <span className={styles.badge}>{`R: ${rCount}`}</span>
      </div>
    );
  };

  const getStatusIcon = (functions?: FunctionAnalysisFunctionDto[]): React.ReactNode => {
    if (!functions || functions.length === 0) return (
      <span className={styles.statusIcon} title="Dados pendentes">
        <i className="material-symbols-outlined">warning</i>
      </span>
    );
    const complete = functions.every(fn => (fn.requirements?.length ?? 0) > 0);
    return (
      <span className={`${styles.statusIcon} ${complete ? styles.statusComplete : ''}`} title={complete ? "Concluído" : "Dados pendentes"}>
        <i className="material-symbols-outlined">{complete ? 'check_circle' : 'warning'}</i>
      </span>
    );
  };

  return items.map(item => ({
    id: item.id,
    label: getEntityName(item, 'Item'),
    type: 'item',
    icon: 'view_column',
    badges: getCountBadges(item.functions),
    statusIcon: getStatusIcon(item.functions),
    children: (item.steps || []).map(step => ({
      id: step.id,
      label: getEntityName(step, 'Etapa'),
      type: 'step',
      icon: 'settings',
      badges: getCountBadges(step.functions),
      statusIcon: getStatusIcon(step.functions),
      children: (step.elements || []).map(el => ({
        id: el.id,
        label: getEntityName(el, 'Elemento'),
        type: 'element',
        icon: el.cause6m != null ? causeIconMap[el.cause6m as Cause6MEnum] : 'category',
        badges: getCountBadges(el.functions),
        statusIcon: getStatusIcon(el.functions),
      }))
    }))
  }));
};

const Step3: React.FC = () => {
  const { fmeaId } = useParams<{ fmeaId: string }>();
  const navigate = useNavigate();
  const { displayToast } = useToast();
  const { userId } = useAuth();
  const { t: translate } = useTranslation('form');

  const [tree, setTree] = useState<FunctionAnalysisTreeDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);

  const [selectedNode, setSelectedNode] = useState<NodeSelection | null>(null);

  const [showFunctionModal, setShowFunctionModal] = useState(false);
  const [functionForm, setFunctionForm] = useState<{ description: string; type: number | null }>({ description: '', type: null });

  const [showRequirementModal, setShowRequirementModal] = useState(false);
  const [targetFunction, setTargetFunction] = useState<FunctionAnalysisFunctionDto | null>(null);
  const [requirementDescription, setRequirementDescription] = useState('');

  const stepHeaderTitle = translate('step3Title') || 'Análise de Funções';
  const stepDescriptionText = translate('step3Description') || 'Associe funções aos itens, etapas e elementos da sua estrutura e defina os requisitos para cada função.';
  const addFunctionLabel = translate('addFunction') || 'Adicionar Função';
  const addRequirementLabel = translate('addRequirement') || 'Adicionar Requisito';
  const selectHint = translate('selectNodeToAnalyze') || 'Selecione um item na estrutura para visualizar e gerenciar suas funções.';
  const noFunctionsMessage = translate('noFunctionsAssigned') || 'Nenhuma função vinculada a este registro.';
  const saveLabel = translate('save') || 'Salvar';
  const cancelLabel = translate('cancel') || 'Cancelar';
  const functionDescriptionLabel = translate('functionDescription') || 'Descrição da Função';
  const functionTypeLabel = translate('functionType') || 'Tipo de Função';
  const requirementDescriptionLabel = translate('requirementDescription') || 'Descrição do Requisito';
  const successTitle = translate('success') || 'Sucesso';
  const errorTitle = translate('error') || 'Erro';
  const warningTitle = translate('attention') || 'Atenção';
  const functionAddedMessage = translate('functionAdded') || 'Função adicionada com sucesso.';
  const requirementAddedMessage = translate('requirementAdded') || 'Requisito adicionado com sucesso.';
  const validationPendingMessage = translate('pendingFunctionsRequirements') || 'Algumas funções ou requisitos estão pendentes.';

  const loadTree = useCallback(async (search?: string) => {
    if (!fmeaId) return;
    try {
      setLoading(true);
      const raw = await FmeaAiagVdaApi.GetStep3FunctionAnalysis(fmeaId, search);
      const mapped = mapFunctionAnalysisTree(raw);
      setTree(mapped);
      if (mapped.completed) setReadOnly(true);
    } catch (err) {
      displayToast('error', errorTitle, translate('errorLoading') || 'Erro ao carregar dados', false, 3000);
    } finally {
      setLoading(false);
    }
  }, [fmeaId, displayToast, errorTitle, translate]);

  useEffect(() => {
    loadTree();
  }, [loadTree]);

  const selectedContext = useMemo((): SelectionContext | null => {
    if (!selectedNode || !tree) return null;
    const item = tree.items.find(i => i.id === selectedNode.itemId);
    if (!item) return null;
    if (selectedNode.type === 'item') return { type: 'item', item };
    const step = (item.steps || []).find(s => s.id === selectedNode.stepId);
    if (!step) return null;
    if (selectedNode.type === 'step') return { type: 'step', item, step };
    const element = (step.elements || []).find(e => e.id === selectedNode.elementId);
    if (!element) return null;
    return { type: 'element', item, step, element };
  }, [selectedNode, tree]);

  const selectedFunctions = useMemo(() => {
    if (!selectedContext) return [];
    if (selectedContext.type === 'item') return selectedContext.item.functions || [];
    if (selectedContext.type === 'step') return selectedContext.step.functions || [];
    return selectedContext.element.functions || [];
  }, [selectedContext]);

  const availableFunctionTypeOptions = useMemo(() => {
    if (selectedContext?.type !== 'item') return [];
    const used = new Set((selectedContext.item.functions || []).map(fn => fn.functionType));
    return [
      { label: 'Função do Item', value: 1 },
      { label: 'Função de Nível Superior', value: 2 },
      { label: 'Função de Nível Inferior', value: 3 },
    ].filter(opt => !used.has(opt.value));
  }, [selectedContext]);

  const handlePrevious = (): void => navigate(`/fmeas/${fmeaId}/steps/2`);

  const openFunctionModal = (): void => {
    setFunctionForm({ description: '', type: null });
    setShowFunctionModal(true);
  };

  const openRequirementModal = (fn: FunctionAnalysisFunctionDto): void => {
    setTargetFunction(fn);
    setRequirementDescription('');
    setShowRequirementModal(true);
  };

  const handleCreateFunction = async (): Promise<void> => {
    if (!selectedContext || !fmeaId || readOnly) return;
    const description = functionForm.description.trim();
    if (!description) return;
    try {
      const stepId = tree?.stepId ?? tree?.analysisId ?? '';
      let targetId: string;
      let targetType: FunctionTargetTypeEnum;
      if (selectedContext.type === 'item') {
        targetId = selectedContext.item.id;
        targetType = FunctionTargetTypeEnum.ProcessItem;
      } else if (selectedContext.type === 'step') {
        targetId = selectedContext.step.id;
        targetType = FunctionTargetTypeEnum.ProcessStep;
      } else {
        targetId = selectedContext.element.id;
        targetType = FunctionTargetTypeEnum.ProcessElement;
      }

      await FmeaAiagVdaApi.CreateFunctionAssociation(fmeaId, {
        stepId,
        targetId,
        targetType,
        description,
        functionType: selectedContext.type === 'item' ? functionForm.type : undefined,
        createdBy: userId,
      });
      displayToast('success', successTitle, functionAddedMessage, false, 2500);
      setShowFunctionModal(false);
      loadTree(searchTerm);
    } catch (err: any) {
      displayToast('error', errorTitle, translate('errorSaving') || 'Erro ao salvar', false, 3000);
    }
  };

  const handleCreateRequirement = async (): Promise<void> => {
    if (!targetFunction || !fmeaId || readOnly) return;
    const description = requirementDescription.trim();
    if (!description) return;
    try {
      await FmeaAiagVdaApi.AddFunctionRequirement(fmeaId, targetFunction.id, {
        description,
        createdBy: userId || '',
      });
      displayToast('success', successTitle, requirementAddedMessage, false, 2500);
      setShowRequirementModal(false);
      loadTree(searchTerm);
    } catch (err: any) {
      displayToast('error', errorTitle, translate('errorSaving') || 'Erro ao salvar', false, 3000);
    }
  };

  const handleComplete = async (): Promise<void> => {
    if (!fmeaId || readOnly) {
       if (readOnly) navigate(`/fmeas/${fmeaId}/steps/4`);
       return;
    }
    setCompleting(true);
    try {
      const validation = await FmeaAiagVdaApi.ValidateFunctionAnalysis(fmeaId);
      if (!validation?.valid) {
        setPendingMessage(validation?.message || validationPendingMessage);
        displayToast('warn', warningTitle, validation?.message || validationPendingMessage, false, 3500);
        return;
      }
      const result = await FmeaAiagVdaApi.CompleteFunctionAnalysis(fmeaId, userId || '');
      if (result?.completed) {
        displayToast('success', successTitle, translate('stepCompleted') || 'Sucesso', false, 2500);
        navigate(`/fmeas/${fmeaId}/steps/4`);
      }
    } catch (err: any) {
      displayToast('error', errorTitle, translate('errorCompleting') || 'Erro ao finalizar', false, 3000);
    } finally {
      setCompleting(false);
    }
  };


  const detailTitle = selectedContext
    ? selectedContext.type === 'item' ? selectedContext.item.name : selectedContext.type === 'step' ? selectedContext.step.name : selectedContext.element.name
    : '';


  return (
    <>
      <StepWrapper
        title={stepHeaderTitle}
        description={stepDescriptionText}
        currentStep={3}
        onPrevious={handlePrevious}
        onNext={handleComplete}
        nextDisabled={completing}
        nextLoading={completing}
      >
        <AnalysisTreePanel
          masterTitle="Estrutura de Funções"
          nodes={useMemo(() => mapToTreeNodes(tree?.items || []), [tree])}
          selectedNodeId={selectedNode?.type === 'element' ? selectedNode.elementId : selectedNode?.type === 'step' ? selectedNode.stepId : selectedNode?.itemId}
          onSelectNode={(id, type) => {
             if (type === 'item') setSelectedNode({ type: 'item', itemId: id });
             else if (type === 'step') {
                 const item = tree?.items.find(i => (i.steps || []).some(s => s.id === id));
                 if (item) setSelectedNode({ type: 'step', itemId: item.id, stepId: id });
             } else if (type === 'element') {
                 const item = tree?.items.find(i => (i.steps || []).some(s => (s.elements || []).some(e => e.id === id)));
                 const step = item?.steps?.find(s => (s.elements || []).some(e => e.id === id));
                 if (item && step) setSelectedNode({ type: 'element', itemId: item.id, stepId: step.id, elementId: id });
             }
          }}
          searchLabel="Buscar na estrutura"
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Buscar por item, etapa ou elemento"
          loading={loading}
          validationMessage={pendingMessage}
          detailContent={
            selectedContext ? (
              <div className={styles.detailWrapper}>
                <div className={styles.detailHeader}>
                  <div className={styles.detailTitleGroup}>
                    <h1 className={styles.detailTitle}>{detailTitle}</h1>
                  </div>
                  {!readOnly && (
                    <Button label={addFunctionLabel} icon="add" severity="success" onClick={openFunctionModal} />
                  )}
                </div>
                
                <div className={styles.functionList}>
                  {selectedFunctions.length > 0 ? (
                    selectedFunctions.map(fn => {
                      const reqCount = fn.requirements?.length || 0;
                      return (
                        <div key={fn.id} className={styles.functionCard}>
                          <div className={styles.functionCardHeader}>
                            <div className={styles.functionInfo}>
                              <span className={styles.functionDescription}>{fn.description}</span>
                              <div className={styles.requirementBadge}>{`Requisitos: ${reqCount}`}</div>
                            </div>
                            {!readOnly && (
                              <Button 
                                label={addRequirementLabel} 
                                icon="add" 
                                variant="text" 
                                severity="success" 
                                onClick={() => openRequirementModal(fn)} 
                              />
                            )}
                          </div>
                          
                          {reqCount > 0 ? (
                            <div className={styles.requirementList}>
                              {fn.requirements!.map(req => (
                                <span key={req.id} className={styles.requirementChip}>{req.description}</span>
                              ))}
                            </div>
                          ) : (
                            <div className={styles.emptyRequirementState}>
                              <span className={styles.emptyText}>Nenhum requisito cadastrado.</span>
                              <div className={styles.orangeAlert}>
                                <i className="material-symbols-outlined">warning</i>
                                <span>Adicione requisitos para concluir a função.</span>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className={styles.emptyFunctions}>
                      <span className={styles.emptyText}>{noFunctionsMessage}</span>
                    </div>
                  )}
                </div>
              </div>
            ) : null
          }
          emptySelectionMessage={selectHint}
        />
      </StepWrapper>

      <Modal
        visible={showFunctionModal}
        onHide={() => setShowFunctionModal(false)}
        header={addFunctionLabel}
        footer={
          <div className="flex gap-2">
            <Button label={cancelLabel} variant="outlined" severity="secondary" onClick={() => setShowFunctionModal(false)} />
            <Button label={saveLabel} severity="success" onClick={handleCreateFunction} disabled={!functionForm.description.trim()} />
          </div>
        }
      >
        <div className={styles.modalContent}>
          <Input 
            type="textArea" 
            label={functionDescriptionLabel} 
            value={functionForm.description} 
            onChange={v => setFunctionForm((p: any) => ({ ...p, description: v }))} 
          />
          {selectedContext?.type === 'item' && (
            <Input 
              type="select" 
              label={functionTypeLabel} 
              value={functionForm.type} 
              options={availableFunctionTypeOptions} 
              onChange={v => setFunctionForm((p: any) => ({ ...p, type: v }))} 
            />
          )}
        </div>
      </Modal>

      <Modal
        visible={showRequirementModal}
        onHide={() => setShowRequirementModal(false)}
        header={addRequirementLabel}
        footer={
          <div className="flex gap-2">
            <Button label={cancelLabel} variant="outlined" severity="secondary" onClick={() => setShowRequirementModal(false)} />
            <Button label={saveLabel} severity="success" onClick={handleCreateRequirement} disabled={!requirementDescription.trim()} />
          </div>
        }
      >
        <div className={styles.modalContent}>
          <p><strong>Função:</strong> {targetFunction?.description}</p>
          <Input 
            type="textArea" 
            label={requirementDescriptionLabel} 
            value={requirementDescription} 
            onChange={setRequirementDescription} 
          />
        </div>
      </Modal>
    </>
  );
};

export default Step3;
